import React, { useState } from 'react';
import { Pencil, Scissors } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * 제자리에서 한 줄 고치기 — 제목·저자처럼 서점에서 딸려 온 값을 다듬는다.
 *
 * 다듬기 제안(suggestions)은 눌러야 적용된다. 자동으로 바꾸지 않는 이유는
 * 쉼표나 괄호가 값의 일부인 책이 있기 때문이다. 원래 값을 아래에 남겨
 * 언제든 되돌릴 수 있게 한다.
 *
 * @param {Array<{label,value}>} suggestions 한 번 눌러 채울 후보
 * @param {function} onSave (next) => Promise
 */
const InlineTextEditor = ({
  label,
  value,
  display,
  suggestions = [],
  onSave,
  rows = 2,
  inputClassName = '',
  editLabel = '수정'
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const trimmed = draft.replace(/\s+/g, ' ').trim();
  const changed = trimmed && trimmed !== value;

  // 지금 값과 같은 제안은 보여줄 이유가 없다
  const usable = suggestions.filter(s => s.value && s.value !== trimmed);

  const open = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!trimmed) {
      toast.error(`${label}은 비워둘 수 없어요`, { id: 'inline-edit' });
      return;
    }
    if (!changed) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(trimmed);
      toast.success(`${label}을 바꿨어요`, { id: 'inline-edit' });
      setEditing(false);
    } catch (error) {
      toast.error(error.message || '변경에 실패했어요', { id: 'inline-edit' });
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-start gap-2">
        {display}
        <button
          onClick={open}
          className="mt-0.5 shrink-0 p-1 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
          aria-label={editLabel}
          title={editLabel}
        >
          <Pencil size={13} strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <span className="label">{label}</span>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          // 한 줄짜리 값이라 줄바꿈을 넣을 일이 없다. Enter 는 저장으로 쓴다
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSave();
          }
          if (e.key === 'Escape') setEditing(false);
        }}
        rows={rows}
        autoFocus
        lang="ko"
        spellCheck={false}
        className={`field mt-2 resize-none ${inputClassName}`}
      />

      {usable.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {usable.map(s => (
            <button
              key={s.label}
              type="button"
              onClick={() => setDraft(s.value)}
              className="chip !py-1.5 !text-xs"
              title={s.value}
            >
              <Scissors size={12} strokeWidth={1.5} />
              {s.label} · {s.value}
            </button>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] leading-relaxed text-ink-500 dark:text-ink-400 font-sans">
        원래 {label}: {value}
      </p>

      <div className="mt-4 flex items-center justify-end gap-2">
        <span className="mr-auto text-[11px] text-ink-500 dark:text-ink-400 font-sans">
          Enter 저장 · Esc 취소
        </span>
        <button onClick={() => setEditing(false)} className="btn-ghost text-xs">취소</button>
        <button
          onClick={handleSave}
          disabled={!trimmed || saving}
          className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? '변경 중...' : '변경'}
        </button>
      </div>
    </div>
  );
};

export default InlineTextEditor;
